import { Observable, of } from 'rxjs';
import { catchError, map, startWith, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpParameterCodec } from '@angular/common/http';

import { Configuration } from './config';
import { HttpRequestState } from './types';

export interface HttpParamsOptions {
  /**
   * String representation of the HTTP parameters in URL-query-string format.
   * Mutually exclusive with `fromObject`.
   */
  fromString?: string;
  /** Object map of the HTTP parameters. Mutually exclusive with `fromString`. */
  fromObject?: {
    [param: string]: string | string[];
  };
  /** Encoding codec used to parse and serialize the parameters. */
  encoder?: HttpParameterCodec;
}

export interface RequestContext {
  queryParams?: HttpParams;
  headers?: HttpHeaders;
}

export interface ApiCallOptions {
  observe?: 'bodyPromise' | 'responsePromise' | 'body' | 'bodyWithState' | 'response' | 'events';
  headers?: Array<[string, any]> | { [key: string]: any };
  reportProgress?: boolean;
  responseType?: 'json' | 'blob' | 'document' | 'arraybuffer';
}

export interface ModuleSourceInfo {
  type: 'file' | 'http';
  path: string;
  hash: string;
  hashDate: string;
}

export class BaseApiClient<T extends Configuration = Configuration> {
  get defaultHeaders(): HttpHeaders {
    return this.config.defaultHeaders;
  }

  readonly basePath: string;

  constructor(protected httpClient: HttpClient, protected config: T) {
    this.basePath = config.basePath.replace(/\/$/, '');
  }

  protected setBasicAuthSecurity(ctx: RequestContext): boolean {
    const username = this.config.username || '';
    const password = this.config.password || '';
    if (username || password) {
      ctx.headers = ctx.headers.set('Authorization', 'Basic ' + btoa(username + ':' + password));
      return true;
    } else {
      ctx.headers = ctx.headers.delete('Authorization');
      return false;
    }
  }

  protected setApiKeySecurity(keyName: string, location: 'header' | 'query', ctx: RequestContext): boolean {
    if (
      (location === 'header' && ctx.headers && ctx.headers.has(keyName)) ||
      (location === 'query' && ctx.queryParams && ctx.queryParams.has(keyName))
    ) {
      return;
    }

    const key = this.config.apiKeys[keyName];
    switch (location) {
      case 'header':
        if (key) {
          ctx.headers = ctx.headers.set(keyName, key);
        } else {
          ctx.headers = ctx.headers.delete(keyName);
        }
        break;
      case 'query':
        if (key) {
          ctx.queryParams = ctx.queryParams.set(keyName, key);
        } else {
          ctx.queryParams = ctx.queryParams.delete(keyName);
        }
        break;
    }
    return !!key;
  }

  protected setOAuth2Security(ctx: RequestContext): boolean {
    const accessToken =
      typeof this.config.accessToken === 'function' ? this.config.accessToken() : this.config.accessToken;
    if (accessToken) {
      ctx.headers = ctx.headers.set('Authorization', `Bearer ${accessToken}`);
      return true;
    } else {
      ctx.headers = ctx.headers.delete('Authorization');
      return false;
    }
  }

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  protected canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (let consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }

  protected createClientOptions(ctx: RequestContext, options: ApiCallOptions) {
    let observe: 'body' | 'response' | 'events';
    switch (options.observe) {
      case 'body':
      case 'response':
      case 'events':
        observe = options.observe;
        break;
      case 'responsePromise':
        observe = 'response';
        break;
      case 'bodyPromise':
        observe = 'body';
        break;
      default:
        observe = 'body';
        break;
    }

    const requestOptions = {
      withCredentials: this.config.withCredentials,
      headers: ctx.headers,
      observe,
      reportProgress: options.reportProgress,
      responseType: options.responseType,
    };
    if (ctx.queryParams) {
      (<any>requestOptions).params = ctx.queryParams;
    }
    return requestOptions;
  }

  protected encodeURI(segment: string): string {
    return encodeURIComponent(String(segment));
  }

  protected setAcceptHeader(ctx: RequestContext, ...mimes: string[]): void {
    const httpHeaderAcceptSelected: string | undefined = this.config.selectHeaderAccept(mimes);
    if (httpHeaderAcceptSelected !== undefined) {
      ctx.headers = ctx.headers.set('Accept', httpHeaderAcceptSelected);
    }
  }

  protected setContentTypeHeader(ctx: RequestContext, ...mimes: string[]): void {
    const httpContentTypeSelected: string | undefined = this.config.selectHeaderContentType(mimes);
    if (httpContentTypeSelected !== undefined) {
      ctx.headers = ctx.headers.set('Content-Type', httpContentTypeSelected);
    }
  }

  protected verifyRequired(...params: Array<[string, any]>): void {
    for (let [key, value] of params) {
      if (value === null || value === undefined) {
        throw new Error(`Required parameter ${key} was null or undefined.`);
      }
    }
  }

  protected createHeaders(objParams: { [key: string]: any }, ...params: Array<[string, any]>): HttpHeaders;
  protected createHeaders(...params: Array<[string, any]>): HttpHeaders;
  protected createHeaders(...params: any[]): HttpHeaders {
    let headers = this.defaultHeaders;
    if (params.length >= 1 && !Array.isArray(params[0])) {
      const obj: { [key: string]: any } = params.shift();
      for (const key in obj) {
        const value = obj[key];
        if (value !== undefined && value !== null) {
          headers = headers.set(key, value);
        }
      }
    }

    for (let [key, value] of params) {
      if (value !== undefined && value !== null) {
        headers = headers.set(key, value);
      }
    }
    return headers;
  }

  protected createQueryParams(...params: Array<[string, string, any] | [string, string, any[], boolean]>): HttpParams {
    const httpParamsOptions: HttpParamsOptions = {};
    if (typeof this.config.encoderFactory === 'function') {
      httpParamsOptions.encoder = this.config.encoderFactory();
    }
    let queryParams = new HttpParams(httpParamsOptions);
    for (const [serializer, key, value, array] of params as Array<[string, string, any, boolean]>) {
      if (value !== undefined && value !== null) {
        if (array === true) {
          for (let v of value) {
            queryParams = queryParams.append(key, v);
          }
        } else {
          if (serializer === 'json') {
            queryParams = queryParams.set(key, this.serializeParam(serializer, value));
          } else {
            // TODO: all other serializers (see below), how do we use them with this HttpParams class?
            queryParams = queryParams.set(key, value);
          }
        }
      }
    }
    return queryParams;
  }

  protected createFormDataParams(...params: Array<[string, string, any] | [string, string, any[], boolean]>): FormData {
    const formData = new FormData();
    for (const [serializer, key, value, array] of params as Array<[string, string, any, boolean]>) {
      // TODO: we need to pass the entire value or items from the value to the serializer
      // right now we can't do it cause the "array" value is not good enough.

      // At the moment this works, but only for form-data and because we only support multi file and app/json only.
      // With www-urlencode this will not work cause we must support specific serializers (i.e. csv, tsv, etc...).
      if (value !== undefined && value !== null) {
        if (serializer === 'json') {
          formData.set(key, this.serializeParam(serializer, value));
        } else if (array === true) {
          for (let v of value) {
            formData.append(key, v);
          }
        } else {
          formData.set(key, value);
        }
      }
    }
    return formData;
  }

  protected serializeParam(serializer: string, value: any, name?: string): any {
    if (!serializer) {
      return value;
    } else {
      switch (serializer) {
        case 'csv':
          if (Array.isArray(value)) {
            return value.join(',');
          } else if (typeof value === 'object' && !!value) {
            return Object.keys(value)
              .map((k) => `${k},${value[k]}`)
              .join(',');
          } else {
            return value;
          }
        case 'multi':
          if (Array.isArray(value)) {
            return value.map((v) => `${name}=${v}`).join('&');
          } else {
            return '';
          }
        case 'ssv':
          if (Array.isArray(value)) {
            return value.join(' ');
          } else {
            return '';
          }
        case 'tsv':
          if (Array.isArray(value)) {
            return value.join('\t');
          } else {
            return '';
          }
        case 'ssv':
          if (Array.isArray(value)) {
            return value.join('|');
          } else {
            return '';
          }
        case 'json':
          // TODO: check value is object!
          return JSON.stringify(value);
      }
    }
  }

  protected toPromise<T>(obs$: Observable<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      let value: any;
      obs$.subscribe(
        (x: T) => (value = x),
        (err: any) => reject(err),
        () => resolve(value)
      );
    });
  }

  protected toHttpRequestState<T>(obs$: Observable<T>): Observable<HttpRequestState<T>> {
    return obs$.pipe(
      map((value) => ({ isLoading: false, value })),
      take(1),
      catchError((error) => of({ isLoading: false, error })),
      startWith({ isLoading: true })
    );
  }
}
